import { useEffect, useState, FC } from "react";
import { useGetImportResult } from "hooks";
import { ImportWizardLoading } from "components/Import";
import { Button, Flex, Paper, Pill, Spacer, Text } from "components";
import { CsvImportType } from "import/types";
import { ImportResultQueryResult } from "generated/graphql";

interface ImportWizardResultProps {
  fileName: string;
  importType: CsvImportType;
  onClickRestart: () => void;
  onSuccess: () => void;
  handleSuccessRedirect: () => void;
  redirectButtonLabel: string;
  taskId: string;
}

export const ImportWizardResult: FC<ImportWizardResultProps> = ({
  fileName,
  importType,
  onClickRestart,
  onSuccess,
  taskId,
  handleSuccessRedirect,
  redirectButtonLabel,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [result, setResult] = useState<NonNullable<ImportResultQueryResult["data"]>["importResult"] | null>(null);

  const { data } = useGetImportResult(taskId, { skip: !isLoading });

  const isDone = data?.importResult?.isDone;

  useEffect(() => {
    if (isDone) {
      setIsLoading(false);
      setResult(data.importResult);
      onSuccess();
    }
  }, [isDone]);

  const fileNamePillCaption = `File: ${fileName}`;

  const hasFailedImports = result?.failedAnimalsUrl || result?.failedActivitiesUrl;

  const handleClickDownloadFailedAnimals = () => {
    if (result?.failedAnimalsUrl) {
      window.open(result?.failedAnimalsUrl, "Download");
    }
  };
  const handleClickDownloadFailedActivities = () => {
    if (result?.failedActivitiesUrl) {
      window.open(result?.failedActivitiesUrl, "Download");
    }
  };

  const numberOfRows = result?.processedRows || 0;
  const numberOfFailedRows = result?.errorRows?.length;
  const numSuccessfullyImportedRows = numberOfRows - numberOfFailedRows;

  const hasImportFailed = numberOfRows === numberOfFailedRows;

  return (
    <>
      {isLoading ? (
        <Paper dark>
          <Flex container containerAlignItems="center" containerDirection="column">
            <Spacer baselineHeight={4} />
            <ImportWizardLoading text="Importing file" />
            <Spacer baselineHeight={4} />
          </Flex>
        </Paper>
      ) : !hasImportFailed ? (
        <Paper colour="green" dark>
          <Flex container containerAlignItems="center" containerDirection="column">
            <Spacer baselineHeight={4} />

            <Text colour="green900">
              <strong>{`${numSuccessfullyImportedRows} Row(s) Imported`}</strong>
            </Text>

            <Spacer baselineHeight={1} />

            <Pill caption={fileNamePillCaption} colour="green" colourInverted />

            <Spacer baselineHeight={1} />
            <Text colour="green900" smallest>
              <strong>
                {importType === "movement"
                  ? "View and complete from the draft movements list"
                  : importType === "animal"
                  ? `${numSuccessfullyImportedRows} Animals Successfully Imported`
                  : `${numSuccessfullyImportedRows} Activities Successfully Imported`}
              </strong>
            </Text>
            <Spacer baselineHeight={4} />
          </Flex>
        </Paper>
      ) : null}

      {!!result?.errorRows?.length ? (
        <>
          <Spacer baselineHeight={2} />

          <Paper colour="red" dark>
            <Flex container containerAlignItems="center" containerDirection="column">
              <Spacer baselineHeight={4} />

              <Text colour="red900">
                <strong>{numberOfFailedRows} Row(s) Failed</strong>
              </Text>

              {result.errorRows.map(({ errors, rowIndex }) => (
                <>
                  <Spacer baselineHeight={1} />

                  {errors.map(({ message }) => (
                    <Text colour="red900" key={rowIndex} smallest>
                      <strong>
                        Row {rowIndex}: {message}
                      </strong>
                    </Text>
                  ))}
                </>
              ))}

              {hasFailedImports ? (
                <>
                  <Spacer baselineHeight={1} />

                  <Flex container containerJustifyContent="center">
                    {result?.failedAnimalsUrl ? (
                      <Flex item itemGutter>
                        <Button
                          caption="Download un-imported animals"
                          colour="grey"
                          onClick={handleClickDownloadFailedAnimals}
                          variant="hollow"
                        />
                      </Flex>
                    ) : null}

                    {result?.failedActivitiesUrl ? (
                      <Flex item itemGutter>
                        <Button
                          caption="Download un-imported activities"
                          colour="grey"
                          onClick={handleClickDownloadFailedActivities}
                          variant="hollow"
                        />
                      </Flex>
                    ) : null}
                  </Flex>
                </>
              ) : null}

              <Spacer baselineHeight={4} />
            </Flex>
          </Paper>
        </>
      ) : null}

      <Spacer baselineHeight={4} />

      <Flex container containerJustifyContent="space-between">
        <Button caption="Start again" colour="grey" onClick={onClickRestart} variant="hollow" />

        <Button caption={redirectButtonLabel} onClick={handleSuccessRedirect} />
      </Flex>
    </>
  );
};
